import { useTranslation } from 'react-i18next';
import { Download } from 'lucide-react';
import type { TicketAttachment } from '../types';
import { useEffect, useState } from 'react';
import { useAuthenticatedMediaUrl } from '../hooks/useAuthenticatedMediaUrl';
import { downloadAuthenticatedFile, fetchAuthenticatedStorage } from '../utils/authenticatedStorage';
import { attachmentKind, audioBlobType } from './media/mediaModel';
import Dialog from './ui/Dialog';
import StateBlock from './ui/StateBlock';

function isImageMime(mime: string): boolean {
  return ['image/jpeg', 'image/png', 'image/gif', 'image/webp'].includes(mime);
}

/**
 * FIX-B43: a recording is stored as .webm, which the file server may label audio/webm (or video/webm for a voice
 * note). The player gets the real type (video for a screen recording, audio for a voice recording).
 */
function useTypedMediaUrl(storageUrl: string, want: 'video' | 'audio' | null, mime: string, name = ''): { url: string; loading: boolean; error: boolean } {
  const [state, setState] = useState({ url: '', loading: want !== null, error: false });
  useEffect(() => {
    if (!want) return undefined;
    let cancelled = false;
    let objectUrl = '';
    setState({ url: '', loading: true, error: false });
    fetchAuthenticatedStorage(storageUrl)
      .then((b) => {
        if (cancelled) return;
        const base = (mime || b.type || want + '/webm').split(';')[0];
        const type = want === 'audio'
          ? audioBlobType(b.type, mime, name)
          : base.startsWith(want + '/') ? base : want + '/' + (base.split('/')[1] || 'webm');
        objectUrl = URL.createObjectURL(new Blob([b], { type }));
        setState({ url: objectUrl, loading: false, error: false });
      })
      .catch(() => { if (!cancelled) setState({ url: '', loading: false, error: true }); });
    return () => { cancelled = true; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [storageUrl, want, mime, name]);
  return state;
}

interface AttachmentLightboxProps {
  att: TicketAttachment;
  onClose: () => void;
}

/**
 * Preview of a ticket attachment (T-44): `ui/Dialog` (focus trap, Escape,
 * focus returns to the file that opened it, a sheet on phones) instead of
 * the hand-rolled full-screen overlay. Images show at full size; other files
 * offer the download.
 */
export default function AttachmentLightbox({ att, onClose }: AttachmentLightboxProps) {
  const { t } = useTranslation();
  const kind = attachmentKind(att.mime_type, att.original_name);
  const timed = kind === 'video' || kind === 'audio' ? kind : null;
  const plain = useAuthenticatedMediaUrl(timed ? undefined : att.url);
  const typed = useTypedMediaUrl(att.url, timed, att.mime_type, att.original_name);
  const { url, loading, error } = timed ? typed : plain;

  return (
    <Dialog
      open
      onClose={onClose}
      title={<bdi className="block truncate">{att.original_name}</bdi>}
      size="xl"
      className="max-w-5xl"
      footer={(
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose}>{t('ticketDetail.close')}</button>
          <button type="button" className="btn btn-primary" onClick={() => void downloadAuthenticatedFile(att.url, att.original_name)}>
            <Download size={16} aria-hidden="true" />
            {t('ticketDetail.download')}
          </button>
        </>
      )}
    >
      <div className="flex min-h-48 items-center justify-center rounded-lg bg-surface-subtle p-2">
        {loading && <StateBlock kind="loading" size="inline" />}
        {error && !loading && <StateBlock kind="error" size="inline" title={t('tickets.fileLoadError')} description={null} />}
        {!loading && !error && url && isImageMime(att.mime_type) && (
          <img src={url} alt={att.original_name} className="max-h-[70vh] max-w-full rounded-lg object-contain" />
        )}
        {!loading && !error && url && timed === 'video' && (
          // A recording someone made: there is no caption file.
          // eslint-disable-next-line jsx-a11y/media-has-caption
          <video src={url} controls playsInline className="max-h-[70vh] max-w-full rounded-lg bg-black" />
        )}
        {!loading && !error && url && timed === 'audio' && (
          // A voice recording someone made: no caption file.
          // eslint-disable-next-line jsx-a11y/media-has-caption
          <audio src={url} controls className="w-full max-w-md" />
        )}
        {!loading && !error && url && !isImageMime(att.mime_type) && !timed && (
          <p className="text-sm text-text-2">{t('ticketDetail.previewNotAvailableV2')}</p>
        )}
      </div>
    </Dialog>
  );
}
